import { useFormikContext } from "formik";
import React from "react";

import { IFinancialParticulars } from "../../../../config/interfaces";
import { FormField, NumericInputField } from "../../../fields";

const BPIRevenueFinancialParticularsForm: React.FC = () => {
  const { values, errors, touched, setFieldValue, handleBlur } =
    useFormikContext<IFinancialParticulars>();

  const getNetIncome = (
    value1: string,
    value2: string,
    value3: string,
    value4: string,
    value5: string,
    value6: string
  ) => {
    const gainsValue =
      Number(value1 || 0) +
      Number(value2 || 0) +
      Number(value3 || 0) +
      Number(value4 || 0) +
      Number(value5 || 0) +
      Number(value6 || 0);

    setFieldValue("TotalLiabilities", gainsValue);
  };

  const getAssetsNetIncome = (
    value1: string,
    value2: string,
    value3: string,
    value4: string,
    value5: string,
    value6: string,
    value7: string
  ) => {
    const gainsValue =
      Number(value1 || 0) +
      Number(value2 || 0) +
      Number(value3 || 0) +
      Number(value4 || 0) +
      Number(value5 || 0) +
      Number(value6 || 0) +
      Number(value7 || 0);

    setFieldValue("TotalAssets", gainsValue);
  };

  return (
    <section className="mb-5">
      <p className="leading-para fw-bold text-primary">Details of Liabilities</p>
      <FormField label="Unsettled Payables" isRequired={true} sm={4}>
        <NumericInputField
          showLeftIcon={true}
          decimalUpto={0}
          name="UnsettledPayables"
          value={values?.UnsettledPayables || ""}
          onChange={(value) => {
            setFieldValue("UnsettledPayables", value);
            getNetIncome(
              value,
              values?.OutstandingSecuredLoans,
              values?.OutstandingUnsecuresLoans,
              values?.Advances,
              values?.CapitalInvestments,
              values.OtherLiabilities
            );
          }}
          onBlur={handleBlur("UnsettledPayables")}
          isInvalid={Boolean(errors.UnsettledPayables) && touched.UnsettledPayables}
          errorMsg={errors.UnsettledPayables}
        />
      </FormField>
      <FormField label="Outstanding Principal - Secured Loans" sm={4}>
        <NumericInputField
          showLeftIcon={true}
          decimalUpto={0}
          name="OutstandingSecuredLoans"
          value={values?.OutstandingSecuredLoans || ""}
          onChange={(value) => {
            setFieldValue("OutstandingSecuredLoans", value);
            getNetIncome(
              values?.UnsettledPayables,
              value,
              values?.OutstandingUnsecuresLoans,
              values?.Advances,
              values?.CapitalInvestments,
              values.OtherLiabilities
            );
          }}
          onBlur={handleBlur("OutstandingSecuredLoans")}
          isInvalid={Boolean(errors.OutstandingSecuredLoans) && touched.OutstandingSecuredLoans}
          errorMsg={errors.OutstandingSecuredLoans}
        />
      </FormField>
      <FormField label="Outstanding Principal - Unsecured Loans" sm={4}>
        <NumericInputField
          showLeftIcon={true}
          decimalUpto={0}
          name="OutstandingUnsecuresLoans"
          value={values?.OutstandingUnsecuresLoans || ""}
          onChange={(value) => {
            setFieldValue("OutstandingUnsecuresLoans", value);
            getNetIncome(
              values?.UnsettledPayables,
              values?.OutstandingSecuredLoans,
              value,
              values?.Advances,
              values?.CapitalInvestments,
              values.OtherLiabilities
            );
          }}
          onBlur={handleBlur("OutstandingUnsecuresLoans")}
          isInvalid={Boolean(errors.OutstandingUnsecuresLoans) && touched.OutstandingUnsecuresLoans}
          errorMsg={errors.OutstandingUnsecuresLoans}
        />
      </FormField>
      <FormField label="Advance" sm={4}>
        <NumericInputField
          showLeftIcon={true}
          decimalUpto={0}
          name="Advances"
          value={values?.Advances || ""}
          onChange={(value) => {
            setFieldValue("Advances", value);
            getNetIncome(
              values?.UnsettledPayables,
              values?.OutstandingSecuredLoans,
              values?.OutstandingUnsecuresLoans,
              value,
              values?.CapitalInvestments,
              values.OtherLiabilities
            );
          }}
          onBlur={handleBlur("Advances")}
          isInvalid={Boolean(errors.Advances) && touched.Advances}
          errorMsg={errors.Advances}
        />
      </FormField>
      <FormField label="Capital Investments" sm={4}>
        <NumericInputField
          showLeftIcon={true}
          decimalUpto={0}
          name="CapitalInvestments"
          value={values?.CapitalInvestments || ""}
          onChange={(value) => {
            setFieldValue("CapitalInvestments", value);
            getNetIncome(
              values?.UnsettledPayables,
              values?.OutstandingSecuredLoans,
              values?.OutstandingUnsecuresLoans,
              values?.Advances,
              value,
              values.OtherLiabilities
            );
          }}
          onBlur={handleBlur("CapitalInvestments")}
          isInvalid={Boolean(errors.CapitalInvestments) && touched.CapitalInvestments}
          errorMsg={errors.CapitalInvestments}
        />
      </FormField>
      <FormField label="Other Liabilities" sm={4}>
        <NumericInputField
          showLeftIcon={true}
          decimalUpto={0}
          name="OtherLiabilities"
          value={values?.OtherLiabilities || ""}
          onChange={(value) => {
            setFieldValue("OtherLiabilities", value);
            getNetIncome(
              values?.UnsettledPayables,
              values?.OutstandingSecuredLoans,
              values?.OutstandingUnsecuresLoans,
              values?.Advances,
              values?.CapitalInvestments,
              value
            );
          }}
          onBlur={handleBlur("OtherLiabilities")}
          isInvalid={Boolean(errors.OtherLiabilities) && touched.OtherLiabilities}
          errorMsg={errors.OtherLiabilities}
        />
      </FormField>
      <FormField label="Total Liabilities" sm={4}>
        <NumericInputField
          showLeftIcon={true}
          isDisabled={true}
          decimalUpto={0}
          name="TotalLiabilities"
          value={values?.TotalLiabilities || ""}
          onChange={(value) => {
            setFieldValue("TotalLiabilities", value);
          }}
          onBlur={handleBlur("TotalLiabilities")}
          isInvalid={Boolean(errors.TotalLiabilities) && touched.TotalLiabilities}
          errorMsg={errors.TotalLiabilities}
        />
      </FormField>
      <p className="leading-para fw-bold text-primary">Details of Assets</p>
      <FormField label="Uncollected Receivables" isRequired={true} sm={4}>
        <NumericInputField
          showLeftIcon={true}
          decimalUpto={0}
          name="UncollectedReceivables"
          value={values?.UncollectedReceivables || ""}
          onChange={(value) => {
            setFieldValue("UncollectedReceivables", value);
            getAssetsNetIncome(
              value,
              values?.TotalInventoryValue,
              values?.CashInHand,
              values?.FixedAssets,
              values?.ClosingBalance,
              values?.LoansAndAdvances,
              values?.OtherAssets
            );
          }}
          onBlur={handleBlur("UncollectedReceivables")}
          isInvalid={Boolean(errors.UncollectedReceivables) && touched.UncollectedReceivables}
          errorMsg={errors.UncollectedReceivables}
        />
      </FormField>
      <FormField label="Total Inventory Value" isRequired={true} sm={4}>
        <NumericInputField
          showLeftIcon={true}
          decimalUpto={0}
          name="TotalInventoryValue"
          value={values?.TotalInventoryValue || ""}
          onChange={(value) => {
            setFieldValue("TotalInventoryValue", value);
            getAssetsNetIncome(
              values?.UncollectedReceivables,
              value,
              values?.CashInHand,
              values?.FixedAssets,
              values?.ClosingBalance,
              values?.LoansAndAdvances,
              values?.OtherAssets
            );
          }}
          onBlur={handleBlur("TotalInventoryValue")}
          isInvalid={Boolean(errors.TotalInventoryValue) && touched.TotalInventoryValue}
          errorMsg={errors.TotalInventoryValue}
        />
      </FormField>

      <FormField label="Cash In Hand" isRequired={true} sm={4}>
        <NumericInputField
          showLeftIcon={true}
          decimalUpto={0}
          name="CashInHand"
          value={values?.CashInHand || ""}
          onChange={(value) => {
            setFieldValue("CashInHand", value);
            getAssetsNetIncome(
              values?.UncollectedReceivables,
              values?.TotalInventoryValue,
              value,
              values?.FixedAssets,
              values?.ClosingBalance,
              values?.LoansAndAdvances,
              values?.OtherAssets
            );
          }}
          onBlur={handleBlur("CashInHand")}
          isInvalid={Boolean(errors.CashInHand) && touched.CashInHand}
          errorMsg={errors.CashInHand}
        />
      </FormField>
      <FormField label="Fixed Assets" sm={4}>
        <NumericInputField
          showLeftIcon={true}
          decimalUpto={0}
          name="FixedAssets"
          value={values?.FixedAssets || ""}
          onChange={(value) => {
            setFieldValue("FixedAssets", value);
            getAssetsNetIncome(
              values?.UncollectedReceivables,
              values?.TotalInventoryValue,
              values?.CashInHand,
              value,
              values?.ClosingBalance,
              values?.LoansAndAdvances,
              values?.OtherAssets
            );
          }}
          onBlur={handleBlur("FixedAssets")}
          isInvalid={Boolean(errors.FixedAssets) && touched.FixedAssets}
          errorMsg={errors.FixedAssets}
        />
      </FormField>
      <FormField label="Closing Balance With Banks" sm={4}>
        <NumericInputField
          showLeftIcon={true}
          decimalUpto={0}
          name="ClosingBalance"
          value={values?.ClosingBalance || ""}
          onChange={(value) => {
            setFieldValue("ClosingBalance", value);
            getAssetsNetIncome(
              values?.UncollectedReceivables,
              values?.TotalInventoryValue,
              values?.CashInHand,
              values?.FixedAssets,
              value,
              values?.LoansAndAdvances,
              values?.OtherAssets
            );
          }}
          onBlur={handleBlur("ClosingBalance")}
          isInvalid={Boolean(errors.ClosingBalance) && touched.ClosingBalance}
          errorMsg={errors.ClosingBalance}
        />
      </FormField>
      <FormField label="Loans And Advances" sm={4}>
        <NumericInputField
          showLeftIcon={true}
          decimalUpto={0}
          name="LoansAndAdvances"
          value={values?.LoansAndAdvances || ""}
          onChange={(value) => {
            setFieldValue("LoansAndAdvances", value);
            getAssetsNetIncome(
              values?.UncollectedReceivables,
              values?.TotalInventoryValue,
              values?.CashInHand,
              values?.FixedAssets,
              values?.ClosingBalance,
              value,
              values?.OtherAssets
            );
          }}
          onBlur={handleBlur("LoansAndAdvances")}
          isInvalid={Boolean(errors.LoansAndAdvances) && touched.LoansAndAdvances}
          errorMsg={errors.LoansAndAdvances}
        />
      </FormField>
      <FormField label="Other Assets" sm={4}>
        <NumericInputField
          showLeftIcon={true}
          decimalUpto={0}
          name="OtherAssets"
          value={values?.OtherAssets || ""}
          onChange={(value) => {
            setFieldValue("OtherAssets", value);
            getAssetsNetIncome(
              values?.UncollectedReceivables,
              values?.TotalInventoryValue,
              values?.CashInHand,
              values?.FixedAssets,
              values?.ClosingBalance,
              values?.LoansAndAdvances,
              value
            );
          }}
          onBlur={handleBlur("OtherAssets")}
          isInvalid={Boolean(errors.OtherAssets) && touched.OtherAssets}
          errorMsg={errors.OtherAssets}
        />
      </FormField>
      <FormField label="Total Assets" sm={4}>
        <NumericInputField
          showLeftIcon={true}
          isDisabled={true}
          decimalUpto={0}
          name="TotalAssets"
          value={values?.TotalAssets || ""}
          onChange={(value) => {
            setFieldValue("TotalAssets", value);
          }}
          onBlur={handleBlur("TotalAssets")}
          isInvalid={Boolean(errors.TotalAssets) && touched.TotalAssets}
          errorMsg={errors.TotalAssets}
        />
      </FormField>
    </section>
  );
};

export default BPIRevenueFinancialParticularsForm;
